import * as Sentry from '@sentry/react';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { SENTRY_DSN, SENTRY_ENVIRONMENT, SENTRY_RELEASE } from './env';
import { supabase } from './supabaseClient';

const rawTracesSampleRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE?.trim() ?? '';
const APP_TENANT = 'mkapp';
const APP_RELEASE_PREFIX = 'web-admin@';

export const SENTRY_TEST_ROUTE = '/__sentry-test';

function normalizeEnvironment(value: string): 'local' | 'staging' | 'production' {
  switch (value.toLowerCase()) {
    case 'production':
      return 'production';
    case 'staging':
      return 'staging';
    default:
      return 'local';
  }
}

function parseSampleRate(value: string): number | undefined {
  if (!value) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function defaultReplaySessionSampleRate(environment: 'local' | 'staging' | 'production'): number {
  return environment === 'production' ? 0.1 : 1.0;
}

function normalizeRelease(value: string): string {
  const release = value.trim();
  if (!release) {
    return `${APP_RELEASE_PREFIX}unknown`;
  }
  return release.includes('@') ? release : `${APP_RELEASE_PREFIX}${release}`;
}

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return normalized.includes('token') ||
    normalized.includes('authorization') ||
    normalized.includes('cookie') ||
    normalized.includes('password') ||
    normalized.includes('secret') ||
    normalized.includes('apikey') ||
    normalized.includes('api_key') ||
    normalized.includes('session') ||
    normalized.includes('credential');
}

function sanitizeInlineSecrets(value: string): string {
  return value
    .replaceAll(
      /((?:authorization|token|password|secret|api[_-]?key)=)([^&\s]+)/gi,
      '$1[redacted]',
    )
    .replaceAll(/(bearer\s+)([^\s]+)/gi, '$1[redacted]');
}

function sanitizeUrl(value: string): string {
  try {
    const url = new URL(value, window.location.origin);
    const toDelete: string[] = [];
    url.searchParams.forEach((_, key) => {
      if (isSensitiveKey(key)) {
        toDelete.push(key);
      }
    });
    toDelete.forEach((key) => url.searchParams.delete(key));
    url.username = '';
    url.password = '';
    return url.toString();
  } catch {
    return sanitizeInlineSecrets(value);
  }
}

function sanitizeValue(key: string, value: unknown): unknown {
  if (value == null) {
    return value;
  }
  if (isSensitiveKey(key)) {
    return '[redacted]';
  }
  if (typeof value === 'string') {
    const sanitized = key.toLowerCase().includes('url')
      ? sanitizeUrl(value)
      : sanitizeInlineSecrets(value);
    return sanitized.length > 512 ? `${sanitized.slice(0, 509)}...` : sanitized;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeValue(key, entry))
      .filter((entry) => entry != null);
  }
  if (typeof value === 'object') {
    return sanitizeRecord(value as Record<string, unknown>);
  }
  return sanitizeInlineSecrets(String(value));
}

function sanitizeRecord(input: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(input)
      .filter(([key]) => !isSensitiveKey(key))
      .map(([key, value]) => [key, sanitizeValue(key, value)])
      .filter(([, value]) => value !== undefined),
  );
}

function isIgnorableWebError(error: unknown): boolean {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'object' && error && 'message' in error
      ? String((error as { message: unknown }).message)
      : String(error);
  const lower = message.toLowerCase();
  return lower.includes('load failed') ||
    lower.includes('failed to fetch') ||
    lower.includes('networkerror when attempting to fetch resource') ||
    lower.includes('network request failed') ||
    lower.includes('the internet connection appears to be offline');
}

export function initSentry() {
  if (!SENTRY_DSN) {
    return;
  }

  const environment = normalizeEnvironment(SENTRY_ENVIRONMENT);

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,
    release: normalizeRelease(SENTRY_RELEASE),
    sendDefaultPii: false,
    tracesSampleRate: parseSampleRate(rawTracesSampleRate),
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.supabaseIntegration({ supabaseClient: supabase }),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    replaysSessionSampleRate: defaultReplaySessionSampleRate(environment),
    replaysOnErrorSampleRate: 1.0,
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    beforeBreadcrumb(breadcrumb) {
      return {
        ...breadcrumb,
        data: breadcrumb.data
          ? sanitizeRecord(breadcrumb.data as Record<string, unknown>)
          : breadcrumb.data,
        message: breadcrumb.message ? sanitizeInlineSecrets(breadcrumb.message) : breadcrumb.message,
      };
    },
    beforeSend(event) {
      const message = event.exception?.values?.[0]?.value ?? event.message ?? '';
      if (typeof message === 'string' && isIgnorableWebError(message)) {
        return null;
      }

      if (event.request) {
        event.request.headers = sanitizeRecord(
          (event.request.headers ?? {}) as Record<string, unknown>,
        ) as Record<string, string>;
        event.request.data = sanitizeValue('request_body', event.request.data);
        if (typeof event.request.url === 'string') {
          event.request.url = sanitizeUrl(event.request.url);
        }
      }

      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => (
          breadcrumb.data
            ? {
                ...breadcrumb,
                data: sanitizeRecord(breadcrumb.data as Record<string, unknown>),
                message: breadcrumb.message
                  ? sanitizeInlineSecrets(breadcrumb.message)
                  : breadcrumb.message,
              }
            : breadcrumb
        ));
      }

      return event;
    },
  });

  Sentry.setTag('platform', 'react');
  Sentry.setTag('app', 'admin-web');
  Sentry.setTag('tenant', APP_TENANT);
  Sentry.metrics.count('admin_web.init', 1, {
    attributes: {
      environment,
    },
  });
}

export function setSentryUser(input?: {
  id: string;
  role?: string;
  tenant?: string;
}) {
  if (!input?.id) {
    Sentry.setUser(null);
    Sentry.setContext('auth', null);
    return;
  }

  Sentry.setUser({ id: input.id });
  Sentry.setContext('auth', {
    role: input.role ?? 'member',
    tenant: input.tenant ?? APP_TENANT,
  });
}

export function captureAppError(error: unknown, context?: string) {
  if (isIgnorableWebError(error)) {
    return;
  }

  Sentry.metrics.count(
    'admin_web.error_captured',
    1,
    context
      ? {
          attributes: {
            context,
          },
        }
      : undefined,
  );
  Sentry.captureException(error, {
    tags: {
      platform: 'react',
      ...(context ? { context } : {}),
    },
  });
}

export function captureDistributionMetric(
  name: string,
  value: number,
  attributes?: Record<string, string | number | boolean>,
) {
  Sentry.metrics.distribution(name, value, {
    attributes,
  });
}

export function sentryTestRouteEnabled() {
  return import.meta.env.DEV && import.meta.env.VITE_ENABLE_SENTRY_TEST_ROUTE === 'true';
}
